import { Delete, Upload } from '@mui/icons-material';
import { CircularProgress, TextField, TextFieldProps } from '@mui/material';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import usePagebuilder from '../../../../../hooks/pagebuilder/usePagebuilder';
import { Network, NetworkProps } from '../../../../../interfaces/interfaces';
import { NetworkComponentInterface } from '../../../../../interfaces/pagebuilder';
import { fetchAndSetNetworks, setNetworks } from '../../../../../reduxToolkit/slices/commonSlice';
import { useSelector, useDispatch } from '../../../../../reduxToolkit/typedRedux';
import { createDisplayLink } from '../../../../../services/helper';
import { createNetwork, updateNetwork } from '../../../../../services/services';
import VyTooltip from '../../../../common/VyTooltip';
import VyUpload from '../../../../common/VyUpload';
import InlineEditButton from '../../../pageBuilder/InlineEditButton';
import InlineSaveButton from '../../../pageBuilder/InlineSaveButton';
import VyTextField from '../../../../common/VyTextField';

interface Props {
  network: Network;
  setIsEditMode?: (value: boolean) => void;
  uiComponent?: NetworkComponentInterface;
}

const NetworkRoomInlineEdit = ({ network, uiComponent, setIsEditMode }: Props) => {
  const event = useSelector((state) => state.common.event);
  const networks = useSelector((state) => state.common.networks);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const users =
    network._id !== 'blank' ? network.users.filter((user) => !user.organization || !network.booth || user.organization !== network.booth?.organization) : [];

  const { register, handleSubmit, control, getValues } = useForm();
  const { editComponent } = usePagebuilder();

  async function handleUpdateNetwork(props: Partial<NetworkProps>) {
    if (!event) return;
    if (!network) return;
    setLoading(true);
    const { name, participantlimit, subtitle, subtext, booth, description, image, bannerImage } = props;

    const networkProps = {
      name: name!,
      participantlimit: parseInt(`${participantlimit}`),
      subtitle,
      subtext,
      booth,
      description,
      image,
      bannerImage,
    };

    if (network._id === 'blank') {
      const newNetwork = await createNetwork(event?._id, networkProps);
      if (uiComponent?.networks && uiComponent.networks.length > 0) {
        const sortIndex = uiComponent.networks.length;
        const isVisible = !!uiComponent.networks.find((network) => network.visible);
        editComponent({ ...uiComponent, networks: [...uiComponent.networks, { networkid: newNetwork._id, sortIndex, visible: isVisible }] });
      }
    } else {
      await updateNetwork(network?._id, networkProps);
    }
    dispatch(fetchAndSetNetworks(event?._id));
    setIsEditMode?.(false);
    setLoading(false);
  }

  const closeEditMode = () => {
    if (network._id === 'blank') {
      dispatch(setNetworks([...networks.filter((n) => n._id !== 'blank')]));
    } else setIsEditMode?.(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleUpdateNetwork)} className="relative inline-flex flex-col w-full p-px mb-8 text-left bg-white rounded max-w-[600px]">
        {loading && (
          <div className="absolute top-0 left-0 z-10 grid w-full h-full bg-white bg-opacity-50 place-items-center">
            <CircularProgress />
          </div>
        )}
        <Controller
          control={control}
          name="bannerImage"
          defaultValue={network.bannerImage}
          render={({ field: { onChange, value } }) => (
            <VyUpload
              hideFileDetails
              hideUpload
              showCropButton={false}
              withMedia={false}
              withCrop
              randomExternalImageButton={false}
              defaultFileId={value}
              onChange={(fileId) => onChange(fileId)}
              cropAspectRatio={6}>
              {value ? (
                <div style={{ minHeight: '100px' }} className="relative">
                  <img
                    style={{ borderColor: uiComponent?.customStyling?.bannerBorderColor || '#ffffff' }}
                    className="mb-1 border-2 cursor-pointer"
                    src={createDisplayLink(value, undefined, undefined)}
                  />
                  <VyTooltip title={t('common:delete')}>
                    <div
                      style={{ background: 'white' }}
                      onClick={(e) => {
                        e.preventDefault();
                        onChange(null);
                      }}
                      className="absolute bottom-0 left-0 p-1 m-2 bg-white rounded cursor-pointer">
                      <Delete className="w-6 h-6 font-black" />
                    </div>
                  </VyTooltip>
                </div>
              ) : (
                <div className="flex items-center justify-center w-full border-2 border-dashed cursor-pointer h-28">
                  <Upload />
                </div>
              )}
            </VyUpload>
          )}
        />
        <div className="flex flex-col gap-4 p-4">
          <div className="flex">
            <Controller
              control={control}
              name="image"
              defaultValue={network.image}
              render={({ field: { onChange, value } }) => (
                <VyUpload
                  hideFileDetails
                  hideUpload
                  showCropButton={false}
                  withMedia={false}
                  withCrop
                  randomExternalImageButton={false}
                  cropShape="round"
                  defaultFileId={value}
                  onChange={(fileId) => onChange(fileId)}>
                  <img
                    src={createDisplayLink(value, undefined, undefined, undefined, undefined, undefined, true)}
                    style={{ height: '54px' }}
                    className="w-auto mr-4 rounded cursor-pointer templateBorder"
                  />
                </VyUpload>
              )}
            />
            <Controller
              control={control}
              name="name"
              defaultValue={network.name}
              rules={{ required: true }}
              render={({ field }) => <VyTextField {...field} inputProps={{ maxLength: 150 }} label={t('networking:roomname')} />}
            />
          </div>
          <Controller
            control={control}
            name="description"
            defaultValue={network.description}
            render={({ field }) => <VyTextField {...field} inputProps={{ maxLength: 400 }} showLength label={t('networking:description')} />}
          />
          <div className="flex justify-between">
            <div className="w-full">
              <Controller
                control={control}
                name="subtitle"
                defaultValue={network.subtitle}
                render={({ field }) => <VyTextField {...field} inputProps={{ maxLength: 150 }} showLength label={t('networking:title')} />}
              />
              <div className="flex mt-4 text-lg font-bold">
                <Controller
                  control={control}
                  name="subtext"
                  defaultValue={network.subtext}
                  render={({ field }) => <VyTextField {...field} inputProps={{ maxLength: 150 }} showLength label={t('networking:subtext')} />}
                />
                <div className="w-full ml-2 font-normal ">
                  <Controller
                    control={control}
                    name="participantlimit"
                    defaultValue={network.settings.userlimit}
                    render={({ field }) => (
                      <VyTextField
                        {...field}
                        label={`${t('networking:participantlimit')} (Max:5000)`}
                        type="number"
                        InputProps={{ inputProps: { min: 1, max: 5000 } }}
                        showLength
                      />
                    )}
                  />
                </div>
              </div>
            </div>
            {network.settings.userlimit > users.length && (
              <div className="flex flex-col-reverse">
                <button disabled className="self-center px-5 py-1 rounded disabled:opacity-50 disabled:cursor-default templateBorder network-button">
                  + Join
                </button>
              </div>
            )}
          </div>
          <InlineSaveButton />
          <InlineEditButton isEditMode={true} setIsEditMode={() => closeEditMode()} tooltip={t('common:cancel')} tooltipPlacement="top" forceShow />
        </div>
      </form>
    </>
  );
};

export default NetworkRoomInlineEdit;
